import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, ArrowLeft, HelpCircle, Shield, Clock } from "lucide-react";
import { toast } from "sonner";
import PageMeta from "../components/PageMeta";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Reset link sent (check your email or backend logs).");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-sky-50 px-4 py-10">
      <PageMeta
        title="Reset your HeDesk password"
        description="Forgot your HeDesk password? Enter your email and we'll send you a secure reset link so you can get back to managing your clinic."
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <img
            src="/logo.jpeg"
            alt="HeDesk"
            className="w-10 h-10 rounded-[22%] object-cover"
          />
          <span className="font-bold text-2xl text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>
                Check your email
              </h1>
              <p className="text-sm text-slate-600 mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. The link expires in 1 hour.
              </p>
              <p className="text-xs text-slate-400 mb-6">
                Note: In sandbox mode, emails are limited to verified addresses. Check the backend logs for the reset link as a fallback.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
                Forgot password?
              </h1>
              <p className="text-sm text-slate-600 mb-6">Enter your email to receive a reset link.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="doctor@example.com"
                    data-testid="forgot-email-input"
                    className="mt-1.5 h-11"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>

              <Link to="/login" className="block mt-4 text-sm text-center text-blue-600 hover:underline">
                Back to login
              </Link>
            </>
          )}
        </div>

        {/* SEO-friendly help section */}
        <section className="mt-6 bg-white/60 rounded-2xl border border-slate-200 p-6" aria-label="Password reset help">
          <h2 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <HelpCircle className="w-4 h-4 text-blue-600" /> Need help resetting your HeDesk password?
          </h2>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">
            HeDesk uses secure email-based password resets so only you can recover access to your clinic dashboard. If you don't receive the email within a few minutes, check your spam folder or contact your clinic admin to reissue access.
          </p>
          <dl className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-900">How long is the reset link valid?</dt>
                <dd className="text-sm text-slate-600">Reset links expire 1 hour after they are sent. Request a new one if needed — there is no limit on how many times you can ask for a reset.</dd>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4" />
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-900">Is my patient data still safe?</dt>
                <dd className="text-sm text-slate-600">Yes. Password resets only affect the staff login. All patient records, appointments, prescriptions and invoices remain encrypted and untouched throughout the reset flow.</dd>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-900">I didn't get the email — what now?</dt>
                <dd className="text-sm text-slate-600">Check your spam or promotions folder. If your clinic admin set you up with a custom email domain, also check with your IT team that emails from <span className="font-mono text-xs">onboarding@resend.dev</span> are allow-listed.</dd>
              </div>
            </div>
          </dl>
          <p className="text-xs text-slate-500 mt-5">
            Still stuck? Email your clinic admin or ping HeDesk support — we usually respond within an hour during Indian business hours.
          </p>
        </section>
      </div>
    </div>
  );
}
